/**
 * Web social account linking (redirect flow).
 *
 * On the web the OAuth handshake is a full-page redirect, so the call below only
 * STARTS the flow -- the browser leaves the app and the provider returns the
 * user to `callbackURL` once the account is attached. That callback points back
 * at the Sign-in & Security screen the user started from.
 *
 * Unlike sign-in there is no invite pre-flight: linking attaches a provider to
 * an account that already exists, and the invite gate guards account CREATION
 * only (docs/adr/0002-invite-gate-at-account-creation.md).
 */

import { authClient } from '@/config/auth-client';
import { ENV } from '@/config/env';
import { toLinkSocialError } from './link-social-error';
import type { SocialProvider } from './link-social.types';

// Metro resolves `./link-social` to this file on web, bypassing the base
// barrel, so the shared error class must be re-exported from BOTH platform
// files or `import { LinkSocialError } from '@/features/auth/link-social'`
// silently resolves to `undefined` at runtime.
export { LinkSocialError } from './link-social-error';

/** The in-app route the provider returns the user to. */
const SECURITY_ROUTE = '/security';

/** Where the provider redirects back to once the link completes. */
const getCallbackURL = (): string => {
  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : ENV.AUTH_URL;
  return `${origin}${SECURITY_ROUTE}`;
};

async function linkSocial(provider: SocialProvider): Promise<void> {
  const result = (await authClient.linkSocial({
    provider,
    callbackURL: getCallbackURL(),
  })) as { error?: { code?: string; message?: string } } | undefined;

  if (result?.error) {
    throw toLinkSocialError(result.error);
  }
}

export function linkGoogle(): Promise<void> {
  return linkSocial('google');
}

export function linkApple(): Promise<void> {
  return linkSocial('apple');
}
